// External Intake & Collection v1.0 PR 2 — the two-prompts panel on the
// template-edit page. Each prompt has a textarea + Save button + an
// "Improve with AI" trigger that opens the drafting modal. Save is only
// enabled on a saved template (the parent disables this panel for the
// unsaved new-template flow).

import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { Button, Textarea, Card, CardContent } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import {
  collectionPromptsApi,
  type CollectionPromptDto,
  type CollectionPromptKind,
} from "@/api/collectionTemplates";
import { CollectionPromptDraftingModal } from "./CollectionPromptDraftingModal";

interface CollectionPromptEditorProps {
  templateId: string;
  templateName: string;
  initialPrompts: CollectionPromptDto[];
}

const KIND_LABELS: Record<CollectionPromptKind, { title: string; description: string }> = {
  Feedback: {
    title: "Feedback prompt",
    description:
      "Runs at upload — advisory guidance shown to the recipient before they submit. Friendly + concrete; coaches, never blocks.",
  },
  Assessment: {
    title: "Assessment prompt",
    description:
      "Runs at submission — produces the distilled key-field summary shown to the Loan Administrator in the staging queue.",
  },
};

export function CollectionPromptEditor({
  templateId,
  templateName,
  initialPrompts,
}: CollectionPromptEditorProps) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-space-4">
      {(["Feedback", "Assessment"] as const).map((kind) => (
        <PromptCard
          key={kind}
          templateId={templateId}
          templateName={templateName}
          kind={kind}
          initial={initialPrompts.find((p) => p.kind === kind) ?? null}
        />
      ))}
    </div>
  );
}

interface PromptCardProps {
  templateId: string;
  templateName: string;
  kind: CollectionPromptKind;
  initial: CollectionPromptDto | null;
}

function PromptCard({ templateId, templateName, kind, initial }: PromptCardProps) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [body, setBody] = useState(initial?.body ?? "");
  const [version, setVersion] = useState(initial?.version ?? 0);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    setBody(initial?.body ?? "");
    setVersion(initial?.version ?? 0);
  }, [initial?.id, initial?.body, initial?.version]);

  const dirty = body.trim() !== (initial?.body ?? "").trim();
  const canSave = !!body.trim() && !saving;
  const meta = KIND_LABELS[kind];

  async function handleSave() {
    if (!canSave) return;
    setSaving(true);
    try {
      const saved = await collectionPromptsApi.save(templateId, kind, body.trim());
      setVersion(saved.version);
      // The edit page reads prompts under this key — stale-mark it so a
      // re-mount shows the saved body, not the pre-save fetch.
      void qc.invalidateQueries({ queryKey: ["collection-template-prompts", templateId] });
      toast({ tone: "success", message: `${meta.title} v${saved.version} saved.` });
    } catch (err) {
      toast({
        tone: "error",
        message: err instanceof Error ? err.message : `Could not save the ${kind} prompt.`,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-space-3 p-space-4">
        <header>
          <h3 className="text-h5 font-display text-onyx m-0">{meta.title}</h3>
          <p className="text-caption text-gray-mid mt-space-1 m-0">{meta.description}</p>
          {version > 0 && (
            <p className="text-caption text-gray-mid mt-space-1">Saved as v{version}.</p>
          )}
        </header>
        <Textarea
          rows={12}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={
            kind === "Feedback"
              ? "Write the feedback prompt body Claude should use at upload time…"
              : "Write the assessment prompt body Claude should use at submission time…"
          }
          aria-label={`${meta.title} body`}
        />
        <div className="flex items-center justify-between gap-space-2">
          <Button
            variant="secondary"
            size="sm"
            type="button"
            onClick={() => setShowModal(true)}
          >
            <Sparkles size={14} className="mr-space-1" />
            Draft with AI
          </Button>
          <Button type="button" disabled={!canSave || !dirty} onClick={handleSave}>
            {saving ? "Saving…" : dirty ? "Save prompt" : "Saved"}
          </Button>
        </div>
      </CardContent>
      {showModal && (
        <CollectionPromptDraftingModal
          templateId={templateId}
          templateName={templateName}
          kind={kind}
          onAcceptDraft={(drafted) => setBody(drafted)}
          onClose={() => setShowModal(false)}
        />
      )}
    </Card>
  );
}
